import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartBar, 
  faInfinity, 
  faSpinner, 
  faExclamationTriangle,
  faQuestionCircle,
  faBookOpen,
  faRobot,
  faGraduationCap,
  faCheckCircle,
  faTimesCircle
  , faArrowTrendUp
} from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../services/apiInterceptor';
import Footer from './Footer';
import './Usage.css';

const Usage = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [usageMetrics, setUsageMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUsageMetrics();
  }, []);

  const fetchUsageMetrics = async () => {
    try {
      setLoading(true);
      setError(null);
      const metrics = await apiRequest('/api/metrics');
      setUsageMetrics(Array.isArray(metrics) ? metrics : []);
    } catch (err) {
      console.error('Error fetching usage metrics:', err);
      setError('Failed to load usage metrics. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getUsageIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'ai':
        return faRobot;
      case 'question':
        return faBookOpen;
      case 'practicetest':
        return faGraduationCap;
      case 'simulatedexam':
        return faCheckCircle;
      case 'osce':
        return faQuestionCircle;
      default:
        return faChartBar;
    }
  };

  const getUsageColor = (usedCount, totalCount, dailyUsed, dailyLimit, monthlyUsed, monthlyLimit) => {
    // Determine which limit to use (daily, monthly, or total)
    let used, total;
    
    if (dailyLimit && dailyLimit > 0) {
      used = dailyUsed || 0;
      total = dailyLimit;
    } else if (monthlyLimit && monthlyLimit > 0) {
      used = monthlyUsed || 0;
      total = monthlyLimit;
    } else if (totalCount && totalCount > 0) {
      used = usedCount || 0;
      total = totalCount;
    } else {
      return 'unlimited'; // Unlimited
    }
    
    if (total === 0) return 'unlimited';
    const percentage = (used / total) * 100;
    if (percentage >= 90) return 'critical';
    if (percentage >= 75) return 'warning';
    if (percentage >= 50) return 'caution';
    return 'good';
  };

  const formatUsageText = (usedCount, totalCount, dailyUsed, dailyLimit, monthlyUsed, monthlyLimit) => {
    // Determine which limit to show (daily, monthly, or total)
    if (dailyLimit && dailyLimit > 0) {
      return dailyLimit === 0 ? (
        <span className="usage-unlimited">
          <FontAwesomeIcon icon={faInfinity} className="infinity-icon" />
          Unlimited
        </span>
      ) : `${dailyUsed || 0} / ${dailyLimit}`;
    } else if (monthlyLimit && monthlyLimit > 0) {
      return monthlyLimit === 0 ? (
        <span className="usage-unlimited">
          <FontAwesomeIcon icon={faInfinity} className="infinity-icon" />
          Unlimited
        </span>
      ) : `${monthlyUsed || 0} / ${monthlyLimit}`;
    } else if (totalCount && totalCount > 0) {
      return totalCount === 0 ? (
        <span className="usage-unlimited">
          <FontAwesomeIcon icon={faInfinity} className="infinity-icon" />
          Unlimited
        </span>
      ) : `${usedCount || 0} / ${totalCount}`;
    } else {
      return (
        <span className="usage-unlimited">
          <FontAwesomeIcon icon={faInfinity} className="infinity-icon" />
          Unlimited
        </span>
      );
    }
  };

  const getPercentage = (usedCount, totalCount, dailyUsed, dailyLimit, monthlyUsed, monthlyLimit) => {
    // Determine which limit to use for percentage calculation
    let used, total;
    
    if (dailyLimit && dailyLimit > 0) {
      used = dailyUsed || 0;
      total = dailyLimit;
    } else if (monthlyLimit && monthlyLimit > 0) {
      used = monthlyUsed || 0;
      total = monthlyLimit;
    } else if (totalCount && totalCount > 0) {
      used = usedCount || 0;
      total = totalCount;
    } else {
      return 100; // Show full bar for unlimited
    }
    
    if (total === 0) return 100;
    return Math.min((used / total) * 100, 100);
  };

  const getPeriodText = (dailyLimit, monthlyLimit, totalLimit) => {
    if (dailyLimit && dailyLimit > 0) {
      return 'per day';
    } else if (monthlyLimit && monthlyLimit > 0) {
      return 'per month';
    } else if (totalLimit && totalLimit > 0) {
      return 'total';
    } else {
      return '';
    }
  };

  const getRemainingCount = (usedCount, totalCount, dailyUsed, dailyLimit, monthlyUsed, monthlyLimit) => {
    if (dailyLimit && dailyLimit > 0) {
      return Math.max(0, dailyLimit - (dailyUsed || 0));
    } else if (monthlyLimit && monthlyLimit > 0) {
      return Math.max(0, monthlyLimit - (monthlyUsed || 0));
    } else if (totalCount && totalCount > 0) {
      return Math.max(0, totalCount - (usedCount || 0));
    } else {
      return null; // Unlimited
    }
  };

  const formatFeatureName = (type, description) => {
    if (description) {
      return description;
    }
    
    switch (type?.toLowerCase()) {
      case 'ai':
        return 'AI Assistance';
      case 'question':
        return 'Question Access';
      case 'practicetest':
        return 'Practice Tests';
      case 'simulatedexam':
        return 'Simulated Exams';
      case 'osce':
        return 'OSCE Simulations';
      case 'adaptivestrength':
        return 'Adaptive Strength Training';
      case 'performanceanalytics':
        return 'Performance Analytics';
      default:
        return type || 'Feature';
    }
  };

  if (loading) {
    return (
      <div className={`usage-page-wrapper ${isDarkMode ? 'dark-mode' : ''}`}>
        <div className="usage-container">
          <div className="usage-loading">
            <FontAwesomeIcon icon={faSpinner} spin className="loading-icon" />
            <p>Loading your usage metrics...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`usage-page-wrapper ${isDarkMode ? 'dark-mode' : ''}`}>
        <div className="usage-container">
          <div className="usage-error">
            <FontAwesomeIcon icon={faExclamationTriangle} className="error-icon" />
            <h3>Unable to Load Usage Data</h3>
            <p>{error}</p>
            <button 
              className="retry-button"
              onClick={fetchUsageMetrics}
            >
              Try Again
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className={`usage-page-wrapper ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="usage-container">
        <div className="usage-header">
          <div className="usage-title-section">
            <FontAwesomeIcon icon={faChartBar} className="usage-header-icon" />
            <div>
              <h1>Usage & Metrics</h1>
              <p>Track your plan usage and available features</p>
            </div>
          </div>
        </div>

        {usageMetrics.length === 0 ? (
          <div className="no-usage-data">
            <FontAwesomeIcon icon={faQuestionCircle} className="no-data-icon" />
            <h3>No Usage Data Available</h3>
            <p>Your usage metrics will appear here once you start using ExamFlip features.</p>
          </div>
        ) : (
          <div className="usage-grid">
            {usageMetrics.map((metric, index) => {
              // Check if the feature is disabled
              if (!metric.included) {
                return (
                  <div key={index} className="usage-card disabled">
                    <div className="usage-card-header">
                      <div className="usage-card-icon">
                        <FontAwesomeIcon 
                          icon={getUsageIcon(metric.type)} 
                          className="feature-icon disabled"
                        />
                      </div>
                      <div className="usage-card-title">
                        <h3>{formatFeatureName(metric.type, metric.description)}</h3>
                      </div>
                    </div>

                    <div className="usage-card-content">
                      <div className="not-included-message">
                        <FontAwesomeIcon icon={faTimesCircle} className="not-included-icon" />
                        <span>Not included in this plan</span>
                      </div>
                    </div>

                    <div className="usage-card-footer">
                      {metric.planName && (
                        <span className="plan-name">
                          Plan: {metric.planName}
                        </span>
                      )}

                      <button
                        className="upgrade-plan-btn small"
                        onClick={() => navigate('/billing')}
                        title="Upgrade your plan"
                      >
                        <FontAwesomeIcon icon={faArrowTrendUp} />
                        &nbsp;Upgrade Plan
                      </button>
                    </div>
                  </div>
                );
              }

              // Feature is included, show normal usage metrics
              const usageColor = getUsageColor(
                metric.usage, 
                metric.limit, 
                metric.dailyUsage, 
                metric.dailyLimit, 
                metric.monthlyUsage, 
                metric.monthlyLimit
              );
              const percentage = getPercentage(
                metric.usage, 
                metric.limit, 
                metric.dailyUsage, 
                metric.dailyLimit, 
                metric.monthlyUsage, 
                metric.monthlyLimit
              );
              const periodText = getPeriodText(metric.dailyLimit, metric.monthlyLimit, metric.limit);
              const remainingCount = getRemainingCount(
                metric.usage, 
                metric.limit, 
                metric.dailyUsage, 
                metric.dailyLimit, 
                metric.monthlyUsage, 
                metric.monthlyLimit
              );
              
              return (
                <div key={index} className={`usage-card ${usageColor}`}>
                  <div className="usage-card-header">
                    <div className="usage-card-icon">
                      <FontAwesomeIcon 
                        icon={getUsageIcon(metric.type)} 
                        className="feature-icon"
                      />
                    </div>
                    <div className="usage-card-title">
                      <h3>{formatFeatureName(metric.type, metric.description)}</h3>
                      {periodText && (
                        <span className="usage-period">
                          {periodText}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="usage-card-content">
                    <div className="usage-stats">
                      <div className="usage-count">
                        {formatUsageText(
                          metric.usage, 
                          metric.limit, 
                          metric.dailyUsage, 
                          metric.dailyLimit, 
                          metric.monthlyUsage, 
                          metric.monthlyLimit
                        )}
                      </div>
                      
                      {(metric.dailyLimit > 0 || metric.monthlyLimit > 0 || metric.limit > 0) && (
                        <div className="usage-percentage">
                          {Math.round(percentage)}% used
                        </div>
                      )}
                    </div>

                    <div className="usage-progress-container">
                      <div className="usage-progress-bar">
                        <div 
                          className={`usage-progress-fill ${usageColor}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>

                    {remainingCount !== null && (
                      <div className="usage-remaining">
                        {remainingCount} remaining
                      </div>
                    )}
                  </div>

                  <div className="usage-card-footer">
                    {metric.planName && (
                      <span className="plan-name">
                        Plan: {metric.planName}
                      </span>
                    )}
                    
                    {/* Show upgrade button when usage is at 100% */}
                    {percentage >= 100 && remainingCount === 0 && (
                      <button
                        className="upgrade-plan-btn small"
                        onClick={() => navigate('/billing')}
                        title="Upgrade your plan to get more access"
                      >
                        <FontAwesomeIcon icon={faArrowTrendUp} />
                        &nbsp;Upgrade
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="usage-info-section">
          <div className="usage-legend">
            <h3>Usage Status Legend</h3>
            <div className="legend-items">
              <div className="legend-item">
                <div className="legend-color good"></div>
                <span>Good (0-49% used)</span>
              </div>
              <div className="legend-item">
                <div className="legend-color caution"></div>
                <span>Caution (50-74% used)</span>
              </div>
              <div className="legend-item">
                <div className="legend-color warning"></div>
                <span>Warning (75-89% used)</span>
              </div>
              <div className="legend-item">
                <div className="legend-color critical"></div>
                <span>Critical (90%+ used)</span>
              </div>
              <div className="legend-item">
                <div className="legend-color unlimited"></div>
                <span>Unlimited</span>
              </div>
              <div className="legend-item">
                <div className="legend-color disabled"></div>
                <span>Not included in plan</span>
              </div>
            </div>
          </div>

          <div className="usage-tips">
            <h3>Usage Tips</h3>
            <ul>
              <li>Monitor your usage to avoid hitting plan limits</li>
              <li>Unlimited features are marked with the ∞ symbol</li>
              <li>Features not included in your plan are shown in gray</li>
              <li>Consider upgrading your plan if you frequently hit limits</li>
              <li>Usage resets based on your plan's billing cycle</li>
            </ul>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Usage;
